<template>
  <component :is="tag" class="fish-frame" :class="[`fish-frame--${tom}`, { 'fish-frame--compacto': compacto }]">
    <header v-if="titulo || $slots.titulo" class="fish-frame__titulo">
      <slot name="titulo">{{ titulo }}</slot>
    </header>

    <div class="fish-frame__miolo">
      <slot />
    </div>

    <footer v-if="$slots.rodape" class="fish-frame__rodape">
      <slot name="rodape" />
    </footer>
  </component>
</template>

<script>
/**
 * 2.1 — `FishFrame`, o painel 9-slice. **A base de todo o resto** (D4).
 *
 * ## A técnica, e por que ela cabe em vinte linhas de CSS
 *
 * É a do `PixelPanel` do War Mages, e o único jeito honesto de esticar uma moldura de pixel art:
 * `border-image` corta os quatro cantos inteiros e **repete** o fio reto entre eles. O painel
 * estica para qualquer tamanho e o ornamento do canto nunca deforma — que é exatamente o que
 * aconteceria com um `background-size: 100% 100%`.
 *
 * Os números (64px, fatia 16) são a geometria do PNG. **Mudou o arquivo, mude aqui e no README, e
 * nunca só num dos dois.**
 *
 * ## O truque da emenda
 *
 * O miolo do `frame-panel.png` é **vazado**, e o fundo vem do `background` do próprio elemento com
 * `background-clip: padding-box`. A emenda entre a borda-imagem e o fundo é invisível não porque
 * alguém alinhou pixel, mas porque **os dois são a mesma cor** — e continuam sendo se a cor mudar,
 * já que os dois saem do mesmo token.
 *
 * ## A regra do D4 que faz isto escalar
 *
 * **Um primitivo não importa store nem service.** Recebe props, emite eventos. Sem isso o
 * componente compartilhado vira dependente de tela e para de ser compartilhado — que é a história
 * de como este cliente chegou a 407 cores e 646 `font-size`.
 */
export default {
  name: 'FishFrame',
  props: {
    /** O elemento de verdade. Um painel que é uma seção da página deve ser `section`, não `div`. */
    tag: { type: String, default: 'div' },
    titulo: { type: String, default: '' },
    /**
     * O material da superfície (D2: UI quente sobre fundo frio).
     *
     * | Tom | O que é | Onde |
     * |---|---|---|
     * | `papel` | pergaminho, tinta escura | **o padrão** — onde se lê: card, painel, carta |
     * | `fundo` | pergaminho um degrau mais claro | painel dentro de painel |
     * | `madeira` | marrom quente, texto claro | estrutura: a folha "Mais", o HUD |
     * | `mar` | azul profundo | o painel que é buraco: a grade do inventário |
     *
     * `agua` continua aceito e resolve para `papel`: era o padrão antigo, e quebrar as telas que já
     * o passam por um nome não valeria nada.
     */
    tom: {
      type: String,
      default: 'papel',
      validator: (v) => ['papel', 'fundo', 'madeira', 'mar', 'agua'].includes(v),
    },
    /** Menos respiro interno. Para painel que mora dentro de outro painel. */
    compacto: { type: Boolean, default: false },
  },
};
</script>

<style scoped>
/**
 * A moldura do pack (`holder.png`, 112×112, fatia 28), com o canto ornamentado dele.
 *
 * `border-width: 14px` contra `border-image-slice: 28` renderiza o canto a **0,5×**. Isso é
 * deliberado: a 1× a moldura teria 28px de espessura, que engole um card pequeno. Meia escala num
 * PNG de pixel art é o único fator fracionário seguro — cada 2 pixels da arte viram 1 na tela, sem
 * amostragem parcial, então nada borra.
 */
.fish-frame {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background-clip: padding-box;
  image-rendering: pixelated;
  text-align: left;
}

/**
 * `papel` é o PADRÃO, e essa é a correção central da paleta.
 *
 * O D2 manda **UI quente sobre fundo frio**: pergaminho e madeira em cima de água profunda. A
 * primeira versão fazia painel azul-escuro sobre fundo azul-escuro — frio sobre frio —, e sem o
 * contraste de temperatura o pack perde a razão de existir e a tela volta a parecer um painel
 * administrativo.
 *
 * O texto vira tinta junto do fundo. Um painel que troca de fundo e não troca de tinta é um painel
 * ilegível, e é o erro mais fácil de cometer ao inverter uma superfície.
 */
.fish-frame--papel,
.fish-frame--agua {
  background: var(--papel);
  color: var(--tinta);
}

/* `madeira`: para o que é ESTRUTURA e não página — a folha "Mais", o painel do HUD, a barra. */
.fish-frame--madeira {
  border-image-source: url('/ui/frame-madeira.png');
  background: var(--madeira);
  color: var(--papel-luz);
}

/* `fundo`: o painel dentro do painel. Pergaminho um degrau mais claro, para se destacar sem virar
 * outra cor — dois marrons diferentes numa tela só já seriam dois materiais. */
.fish-frame--fundo {
  background: var(--papel-luz);
  color: var(--tinta);
}

/* `mar`: o painel que é buraco — a grade do inventário, o fundo de um encaixe grande. */
.fish-frame--mar {
  border-image-source: url('/ui/frame-madeira.png');
  background: var(--fundo-encaixe);
  color: var(--texto);
}

.fish-frame__titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  line-height: var(--lh-curto);
  padding-bottom: var(--e-2);
  margin-bottom: var(--e-3);
  border-bottom: 2px solid var(--papel-sombra);
}

.fish-frame--madeira .fish-frame__titulo,
.fish-frame--mar .fish-frame__titulo {
  border-bottom-color: var(--madeira-escura);
}

.fish-frame__miolo {
  padding: var(--e-1);
}

.fish-frame--compacto .fish-frame__miolo {
  padding: 0;
}

.fish-frame__rodape {
  margin-top: var(--e-3);
  padding-top: var(--e-2);
  border-top: 2px solid var(--papel-sombra);
}

.fish-frame--madeira .fish-frame__rodape,
.fish-frame--mar .fish-frame__rodape {
  border-top-color: var(--madeira-escura);
}
</style>
